/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable security/detect-object-injection */
/* eslint-disable @typescript-eslint/naming-convention */
import { buildAuthSession } from '@bees-web/core/shared/contexts'
import { PageTemplate, PersonaType, SelectedAccountContext } from '@bees-web/core/types'
import { getDeviceTypeFromUserAgent } from '@bees-web/core/utils'
import { type GetServerSideProps } from 'next'
import { FC } from 'react'

import httpClient from '@/core/clients/httpClient'
import Page, { ClientPageProps } from '@/core/components/Page'
import { registerServerActivity } from '@/core/middleware/activityTracker'
import {
  countryNotSupported,
  notAuthorized,
  serviceUnavailable,
} from '@/core/redirects/pageRedirects'
import { TemplateService } from '@/core/services/templateService'
import { CreatePageProps, PageProps, RawPageTemplate } from '@/core/types'
import { generateInitialRequestTraceId } from '@/core/utils/requestTrace'
import {
  getSanitizedQuery,
  usingDeeplink,
  usingLocale,
  usingTrackingData,
} from '@/core/utils/serverSide'
import { handleDevToolsAuth } from '@/features/devtools/utils/devToolsAuth'

import {
  ensureAppConfigInitialized,
  getCountryConfig,
  getDomainLocale,
  isAppConfigInitialized,
} from '../utils/appConfig'
import { isDevelopment } from '../utils/environment'
import { isPotentiallyLowEndDevice } from '../utils/infos'
import { getBlocksServerData } from '../utils/joker'
import { isJokerPageNotFound, isJokerPageRedirect } from '../utils/joker/pageResolution'

type UserInfosResponse = {
  id: string
  emailAddress: string
  phoneNumber: string
  lastName: string
  firstName: string
  country: string
  persona: PersonaType
  isContractVisibilityEnabled: boolean
  selectedAccount: SelectedAccountContext
}

export type CreatedNextPage = {
  Page: FC<ClientPageProps>
  getServerSideProps: GetServerSideProps<PageProps>
}

export function pageFactory(creationProps: CreatePageProps): CreatedNextPage {
  return {
    Page: (props) => (
      <Page
        {...props}
        workspace={creationProps.workspace}
        dependencies={creationProps.dependencies}
      />
    ),
    getServerSideProps: async (context) => {
      if (!isAppConfigInitialized()) {
        await ensureAppConfigInitialized()
      }

      // Register server-side activity for idle tracking
      registerServerActivity()

      const { isSupported, locale, country, language, partnerRedirectPath } =
        getDomainLocale(context)

      if (isSupported) {
        if (partnerRedirectPath) {
          return {
            redirect: { destination: partnerRedirectPath, permanent: false },
          }
        }

        usingLocale(context, country, language)
        const { trackingConsented, sessionId, ajsAnonymousId, ajsUserId } =
          usingTrackingData(context)
        const { accountId, storeId } = usingDeeplink(context)

        let devToolsEnabled = false
        try {
          const authResult = await handleDevToolsAuth(context)
          devToolsEnabled = authResult.isEnabled
        } catch (error) {
          console.error('prod', 'Error in DevTools auth, continuing without DevTools', {
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }

        const { req } = context
        const userAgent = req?.headers['user-agent'] ?? ''
        const deviceType = getDeviceTypeFromUserAgent(userAgent)
        const { template: locolib, options = {} } = creationProps
        const qsRequestTraceId = context.query.requestTraceId as string
        const query = getSanitizedQuery(context.query, deviceType)
        // TODO: make default useCache = true
        const {
          cachePage = false,
          noAuthentication = false,
          processDeferredOnServer: fetchBlocksOnServer = false,
        } = options

        // TODO: remove this in the future
        if (!noAuthentication && !sessionId) {
          return notAuthorized(req?.url)
        }

        const coreQs = cachePage
          ? { locale }
          : {
              ...query,
              locale,
              ...(accountId ? { selectedPocAccount: accountId } : {}),
              ...(storeId ? { storeId } : {}),
            }

        const pageKey = creationProps.pageKey || creationProps.template
        const requestTraceId = generateInitialRequestTraceId(pageKey, qsRequestTraceId)

        const requestOptions = (suffix: string) => ({
          context,
          requestTraceId: `${requestTraceId}:${suffix}`,
        })

        const templatePromise = new TemplateService(
          httpClient<PageTemplate>(requestOptions('template'))
        ).getTemplate(locolib, coreQs, cachePage, devToolsEnabled)
        const userInfosPromise = !noAuthentication
          ? httpClient<UserInfosResponse>(requestOptions('user-info'))('api/user/info')
          : Promise.resolve(undefined)

        const [templateSettled, userInfosSettled] = await Promise.allSettled([
          templatePromise,
          userInfosPromise,
        ])

        let template: RawPageTemplate
        let userInfos: UserInfosResponse | undefined

        if (templateSettled.status === 'rejected') {
          return notAuthorized(req?.url)
        } else {
          template = templateSettled.value
        }

        if (isJokerPageRedirect(template)) {
          return { redirect: template.redirect }
        }

        if (isJokerPageNotFound(template)) {
          return { notFound: true }
        }

        if (userInfosSettled.status === 'rejected')
          console.error('Failed to fetch user infos', userInfosSettled)
        else if (userInfosSettled.status === 'fulfilled')
          userInfos = userInfosSettled.value

        if (template) {
          // TODO: use a more robust way to determine if the device is low-end
          const shouldFetchBlocks =
            fetchBlocksOnServer === 'auto'
              ? isPotentiallyLowEndDevice(req?.headers['user-agent'])
              : fetchBlocksOnServer

          // Fetch blocks on server to be used as cache by SWR on client
          const serverCache = shouldFetchBlocks
            ? await getBlocksServerData(template, context, coreQs, requestTraceId)
            : {}

          if (cachePage) {
            context.res.setHeader(
              'Cache-Control',
              'public, s-maxage=600, stale-while-revalidate=3600'
            )
          }

          const devToolsData = devToolsEnabled
            ? {
                ssrTimestamp: new Date().toISOString(),
                templateLogs: JSON.parse(JSON.stringify(TemplateService.getLogs())),
              }
            : null

          const session = userInfos
            ? buildAuthSession(
                {
                  user: {
                    id: userInfos.id,
                    emailAddress: userInfos.emailAddress,
                    phoneNumber: userInfos.phoneNumber,
                    firstName: userInfos.firstName,
                    lastName: userInfos.lastName,
                    persona: userInfos.persona,
                    country: userInfos.country,
                  },
                  isContractVisibilityEnabled: userInfos.isContractVisibilityEnabled,
                  selectedAccount: userInfos.selectedAccount,
                },
                { accountId, storeId }
              )
            : null

          const { featureFlags: flags, ...templateForPage } = template
          const featureFlags = flags ?? {}

          const countryConfig = getCountryConfig(country)

          const {
            braze,
            segment,
            onetrust,
            brazeInAppConfiguration,
            anonymizedSegmentProperties,
          } = countryConfig

          const serverProps: PageProps = {
            serverCache,
            configuration: {
              braze,
              segment,
              onetrust,
              brazeInAppConfiguration,
              anonymizedSegmentProperties,
            },
            context: {
              session,
              country,
              language,
              namespace: creationProps.workspace.namespace,
              page: {
                key: creationProps.pageKey || template.name,
                trackName:
                  creationProps.trackName || creationProps.pageKey || template.name,
                template: { name: locolib, data: templateForPage },
              },
              query,
              tracking: {
                consented: trackingConsented !== null,
                enabled: trackingConsented ?? false,
                ajsAnonymousId,
                ...(ajsUserId ? { ajsUserId } : {}),
                requestTraceId,
              },
              featureFlags,
              viewport: { deviceType },
            },
            __devToolsEnabled: devToolsEnabled,
            __devToolsData: devToolsData,
          }

          if (isDevelopment) console.info('Using Static Routing System:', serverProps)

          return { props: serverProps }
        } else {
          return serviceUnavailable
        }
      }

      return countryNotSupported
    },
  }
}
